import { useQuery } from '@tanstack/react-query';

import { unwrap } from '@/api/envelope';
import { listOrganizations, type OrganizationSummary } from '@/api/generated';
import { useDebounced } from '@/lib/useDebounced';

/** Fewer than this many letters would match half the list. */
export const MIN_SEARCH_LENGTH = 2;

export interface OrganizationSearch {
  /** The matches, once a search has answered. Undefined before, or if it failed. */
  organizations: OrganizationSummary[] | undefined;
  /** True from the moment there is something to search until the answer is in. */
  searching: boolean;
}

/**
 * Organizations whose name matches, for the top bar's search.
 *
 * The same request the staff list makes with its own search box - deleted
 * ones included, as the list includes them, so a name found in one is found
 * in the other - but five results, and only once typing has paused. A failure
 * leaves `organizations` undefined and nothing else: a typeahead has no place
 * for an error, and the page results beside it are still there.
 */
export function useOrganizationSearch(
  query: string,
  enabled: boolean,
): OrganizationSearch {
  const typed = query.trim();
  const settled = useDebounced(typed, 300);
  const active = enabled && settled.length >= MIN_SEARCH_LENGTH;

  const result = useQuery({
    queryKey: ['staff', 'organizations', 'search', settled],
    enabled: active,
    queryFn: async () => {
      const reply = await listOrganizations({
        query: { search: settled, limit: 5, page: 1, include_deleted: true },
        throwOnError: true,
      });
      return unwrap(reply.data).items ?? [];
    },
  });

  const wanted = enabled && typed.length >= MIN_SEARCH_LENGTH;
  return {
    organizations: active && typed === settled ? result.data : undefined,
    // Still typing counts: the answer on screen would be for older letters.
    searching: wanted && (typed !== settled || result.isFetching),
  };
}
